import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default fallback image (used when no images uploaded)
// Only keeping the mountain landscape - removed 1st and 3rd per user request
const defaultImages = [
  { url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920", position: "center" }
];

const Hero = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [backgroundImages, setBackgroundImages] = useState(defaultImages);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch homepage images from API
  const fetchHomepageImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/homepage-images`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // Store full image data including mobile_url, position, and device_type
          const images = data.map(img => ({
            url: img.url.startsWith('http') ? img.url : `${API_URL}${img.url}`,
            mobile_url: img.mobile_url ? (img.mobile_url.startsWith('http') ? img.mobile_url : `${API_URL}${img.mobile_url}`) : null,
            position: img.position || 'center',
            mobile_position: img.mobile_position || img.position || 'center',
            device_type: img.device_type || 'both' // 'both', 'desktop', 'mobile'
          }));
          setBackgroundImages(images);
        }
      }
    } catch (error) {
      console.log('Using default images');
    }
  }, []);

  useEffect(() => {
    fetchHomepageImages();
  }, [fetchHomepageImages]);

  // Filter images based on current device type
  const filteredImages = backgroundImages.filter(img => {
    const deviceType = img.device_type || 'both';
    if (deviceType === 'both') return true;
    if (isMobile && deviceType === 'mobile') return true;
    if (!isMobile && deviceType === 'desktop') return true;
    return false;
  });

  // Use filtered images for rotation, or fallback to default if none match
  const displayImages = filteredImages.length > 0 ? filteredImages : defaultImages;

  useEffect(() => {
    if (displayImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
        setIsTransitioning(false);
      }, 500);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [displayImages.length]);

  // Reset index when filtered images change
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [isMobile]);

  const handleClick = (path) => {
    navigate(path);
  };

  // Get the appropriate image URL based on device
  const getImageUrl = (img) => {
    if (isMobile && img.mobile_url) {
      return img.mobile_url;
    }
    return img.url;
  };

  // Get the appropriate position based on device
  const getImagePosition = (img) => {
    if (isMobile && img.mobile_position) {
      return img.mobile_position;
    }
    return img.position || 'center';
  };

  return (
    <section className="flex flex-col">
      {/* Hero Image Section */}
      <div className="relative min-h-[calc(100vh-10rem)] md:min-h-[calc(100vh-7rem)] overflow-hidden">
        {/* Background Images with Crossfade */}
        <div className="absolute inset-0 z-0">
          {displayImages.map((img, index) => (
            <img
              key={index}
              src={getImageUrl(img)}
              alt="Travel Adventure"
              className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentImageIndex && !isTransitioning ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ objectPosition: getImagePosition(img) }}
            />
          ))}
          <div className="absolute inset-0 bg-white/20"></div>
        </div>

        {/* Image Indicator Dots */}
        {displayImages.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
            {displayImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex ? 'bg-amber-500 w-6' : 'bg-white/60 hover:bg-white'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Plan Your Trip Buttons - Below the image */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 py-6 flex flex-col sm:flex-row gap-3 justify-center items-center px-4">
        <Button
          onClick={() => handleClick('/instant-itinerary')}
          size="lg"
          data-testid="hero-plan-instant-navmind-btn"
          className="group relative bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-base sm:text-lg px-8 py-6 shadow-xl transition-transform hover:scale-105 rounded-full overflow-hidden">
          <Sparkles className="w-5 h-5 mr-2 animate-pulse" />
          Plan Instant Trip with NavMind
        </Button>
        <Button
          onClick={() => handleClick('/destinations')}
          size="lg"
          data-testid="hero-plan-trip-btn"
          variant="outline"
          className="bg-white border-2 border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white text-base sm:text-lg px-8 py-6 shadow-xl transition-transform hover:scale-105 rounded-full">
          Plan Your Trip
        </Button>
      </div>
    </section>
  );
};

export default Hero;
